import * as React from 'react';
import ReactTooltip from 'react-tooltip';
import Xarrow from 'react-xarrows';
import {
  getActiveDeviceMaskClass,
  getDeviceIndex,
  getOutputDevices,
  hasInputDeviceConnections,
  isDeviceActive,
  getInputDevices,
  onDeviceItemClick,
  connectionArrowsFilterFunction,
  isDeviceDisabled,
  isDeviceGrayed
} from './connections.logic';
import {
  getDeviceIconBySymbol,
  PressProfiles,
  getSnapInIcon
} from '../../util';
import { getAssetPath } from '../../util/images';
import { SvgImage } from '../SvgImage';
import ConnectionDeviceButton from '../ConnectionDeviceButton/ConnectionDeviceButton';

import './connections.scss';
import {
  FixedHeaderColumn,
  MainContainer,
  RoomToggle,
  ScrollableContainer,
  SnapInDescription,
  SnapInImageContainer,
  Row,
  DockRenderWrapper,
  FlexContainer,
  ScrollableWrapper,
  ConnectionDeviceButtonWrapper
} from './ConnectionsComponents';
import { RoomDto } from '../../api';

export interface IBasicConnectionsPanelProps {
  rooms: RoomDto[];
  docks: any;
  updateConnectionsHandler: (connections: Array<{ connection: any }>) => void;
  deviceConnections: Array<{ connection: any }>;
  previewMode: boolean;
}

const BasicConnectionsPanel = (props: IBasicConnectionsPanelProps) => {
  const {
    rooms,
    docks,
    updateConnectionsHandler,
    deviceConnections,
    previewMode
  } = props;
  const [refresh, setRefresh] = React.useState(0);
  const [roomsVisibilityStatus, setRoomsVisibilityStatus] = React.useState<{}>(
    {}
  );
  const [inputDevice, setInputDevice] = React.useState(null);
  const [
    connectionsConfiguration,
    setConnectionsConfiguration
  ] = React.useState([]);

  const arrowsRefreshHandler = () => {
    setRefresh(prev => prev + 1);
  };

  const updateConnections = (connections: Array<{ connection: any }>) => {
    setConnectionsConfiguration(connections);
    updateConnectionsHandler(connections.map(devCon => devCon.connection));
  };

  React.useEffect(() => {
    updateConnections(deviceConnections);
    ReactTooltip.rebuild();
  }, [deviceConnections]);

  return (
    <MainContainer>
      <ScrollableContainer>
        <Row>
          <FixedHeaderColumn>Snap-ins list</FixedHeaderColumn>
          <FlexContainer>
            <ScrollableWrapper
              flexBasis="200"
              display="flex"
              justifyContent="center"
            >
              1. Choose input
            </ScrollableWrapper>
            <ScrollableWrapper
              flexBasis="304"
              display="flex"
              justifyContent="center"
            >
              2. Connect with output
            </ScrollableWrapper>
          </FlexContainer>
        </Row>
        {rooms.map(room => (
          <Row key={`room_${room.id}`}>
            <RoomToggle
              opened={!roomsVisibilityStatus[room.id]}
              roomName={room.name}
              onClick={() => {
                const currentVisibilityStatus = roomsVisibilityStatus[room.id];
                setRoomsVisibilityStatus({
                  ...roomsVisibilityStatus,
                  [room.id]: !currentVisibilityStatus
                });

                setImmediate(() => {
                  ReactTooltip.rebuild();
                  arrowsRefreshHandler();
                });
              }}
            />
            {!roomsVisibilityStatus[room.id] &&
              docks[room.id].map(dock => {
                const SnapInImage = getSnapInIcon(dock.snapInCode);
                return (
                  <FlexContainer key={`dock_${dock.number}`}>
                    <DockRenderWrapper>
                      <FixedHeaderColumn>
                        <SnapInImageContainer>
                          {getActiveDeviceMaskClass(
                            connectionsConfiguration,
                            inputDevice,
                            dock,
                            PressProfiles.singlePress
                          ).map(classes => (
                            <div
                              className={`device-mask ${classes}`}
                              key={classes}
                            />
                          ))}
                          <SnapInImage />
                          <ReactTooltip />
                          <div
                            data-tip={`Zigbee ID: ${dock.zigbeeId ||
                              'unknown'}`}
                          >
                            <SvgImage
                              inAssetsPath={getAssetPath(
                                'icons/devices-location-configuration.svg'
                              )}
                              className="devices-configuration-snap-in-info-icon"
                            />
                          </div>
                        </SnapInImageContainer>
                        <SnapInDescription
                          className="ellipsis"
                          data-tip={`Dock: ${dock.number}`}
                        >
                          Dock: {dock.number}
                        </SnapInDescription>
                        <p className="devices-configuration-snap-in-label">
                          {dock.positionId}
                        </p>
                      </FixedHeaderColumn>
                    </DockRenderWrapper>
                    <FlexContainer>
                      <ScrollableWrapper>
                        {getInputDevices(dock.devices).map(device => {
                          const isActive = isDeviceActive(
                            connectionsConfiguration,
                            inputDevice,
                            device,
                            dock,
                            'input',
                            PressProfiles.singlePress
                          );

                          return (
                            <ConnectionDeviceButtonWrapper
                              key={`device_${device.id}`}
                            >
                              <ConnectionDeviceButton
                                label={device.name}
                                icon={getDeviceIconBySymbol(device.symbol)}
                                deviceLinked={
                                  !isActive &&
                                  hasInputDeviceConnections(
                                    device,
                                    dock,
                                    connectionsConfiguration
                                  )
                                }
                                disabled={
                                  !previewMode &&
                                  isDeviceDisabled(
                                    device,
                                    inputDevice,
                                    'output'
                                  )
                                }
                                grayed={isDeviceGrayed(
                                  previewMode,
                                  isActive,
                                  inputDevice,
                                  dock,
                                  device,
                                  'input'
                                )}
                                activeCircle={{
                                  right:
                                    isActive &&
                                    inputDevice &&
                                    inputDevice.startsFrom === 'input'
                                }}
                                active={isActive}
                                id={`input_${dock.number}_${getDeviceIndex(
                                  dock.devices,
                                  device
                                )}`}
                                onClick={() =>
                                  onDeviceItemClick(
                                    dock,
                                    device,
                                    'input',
                                    inputDevice,
                                    setInputDevice,
                                    PressProfiles.singlePress,
                                    connectionsConfiguration,
                                    updateConnections,
                                    previewMode
                                  )
                                }
                              />
                            </ConnectionDeviceButtonWrapper>
                          );
                        })}
                      </ScrollableWrapper>
                      <ScrollableWrapper>
                        {getOutputDevices(dock.devices).map(device => {
                          const isActive = isDeviceActive(
                            connectionsConfiguration,
                            inputDevice,
                            device,
                            dock,
                            'output',
                            PressProfiles.singlePress
                          );

                          return (
                            <ConnectionDeviceButtonWrapper
                              key={`device_${device.id}`}
                            >
                              <ConnectionDeviceButton
                                label={device.name}
                                icon={getDeviceIconBySymbol(device.symbol)}
                                active={isActive}
                                activeCircle={{
                                  left:
                                    isActive &&
                                    inputDevice &&
                                    inputDevice.startsFrom === 'output'
                                }}
                                disabled={
                                  !previewMode &&
                                  isDeviceDisabled(device, inputDevice, 'input')
                                }
                                grayed={isDeviceGrayed(
                                  previewMode,
                                  isActive,
                                  inputDevice,
                                  dock,
                                  device,
                                  'output'
                                )}
                                id={`output_${dock.number}_${getDeviceIndex(
                                  dock.devices,
                                  device
                                )}`}
                                onClick={() =>
                                  onDeviceItemClick(
                                    dock,
                                    device,
                                    'output',
                                    inputDevice,
                                    setInputDevice,
                                    PressProfiles.singlePress,
                                    connectionsConfiguration,
                                    updateConnections,
                                    previewMode
                                  )
                                }
                              />
                            </ConnectionDeviceButtonWrapper>
                          );
                        })}
                      </ScrollableWrapper>
                    </FlexContainer>
                  </FlexContainer>
                );
              })}
          </Row>
        ))}
        <div className="devices-configuration-arrows-container">
          {connectionsConfiguration
            .filter(({ connection }) =>
              connectionArrowsFilterFunction(
                inputDevice,
                PressProfiles.singlePress,
                connection
              )
            )
            .map(({ connection }) =>
              inputDevice && inputDevice.startsFrom === 'input' ? (
                <Xarrow
                  path="grid"
                  startAnchor="right"
                  endAnchor="left"
                  start={`input_${connection.sourceDockNumber}_${connection.sourceDeviceNumber}`}
                  end={`output_${connection.targetDockNumber}_${connection.targetDeviceNumber}`}
                  key={`${connection.sourceDockNumber}${connection.sourceDeviceNumber}_${connection.targetDockNumber}${connection.targetDeviceNumber}`}
                  strokeWidth={2}
                  headSize={4}
                  // @ts-ignore
                  title={`input_${connection.sourceDockNumber}_${connection.sourceDeviceNumber}_${refresh}`}
                />
              ) : (
                <Xarrow
                  path="grid"
                  startAnchor="left"
                  endAnchor="right"
                  end={`input_${connection.sourceDockNumber}_${connection.sourceDeviceNumber}`}
                  start={`output_${connection.targetDockNumber}_${connection.targetDeviceNumber}`}
                  key={`${connection.targetDockNumber}${connection.targetDeviceNumber}_${connection.sourceDockNumber}${connection.sourceDeviceNumber}`}
                  strokeWidth={2}
                  headSize={4}
                  // @ts-ignore
                  title={`output_${connection.sourceDockNumber}_${connection.sourceDeviceNumber}_${refresh}`}
                />
              )
            )}
        </div>
      </ScrollableContainer>
    </MainContainer>
  );
};

export default BasicConnectionsPanel;
